import { useCallback } from 'react';
import { AllieDownloadIcon } from '../../../../styling/icons';
import {
  AllieReportDownloader,
  AllieReportDownloaderQuery,
  DownloadFormatMenu,
  base64ToJson,
  exportCSVFile,
  exportXLSXFile,
} from '../../../../views/components/ReportDownloader';
import { LoadServerRowsProps } from '../../AllieDataGrid.types';
import { StyledDownloadIcon, StyledIconButton } from './DataGridDownload.styles';
import { AllieText } from '../../../Text';

export interface DataGridDownloadProps {
  query: (params: LoadServerRowsProps) => any;
  title?: string;
  downloadLabel?: string;
  /**
   * When true, renders a format picker (CSV / XLS) instead of a plain CSV-only
   * button. Opt-in and off by default so every other `AllieDataGrid` consumer
   * keeps its current CSV-only download behavior unchanged.
   */
  enableXlsx?: boolean;
}

export const DataGridDownload = ({
  query,
  title,
  downloadLabel,
  enableXlsx,
}: DataGridDownloadProps) => {
  const downloadFile = useCallback(
    async (format: 'csv' | 'xlsx') => {
      const { data, fileName } = await (query as AllieReportDownloaderQuery)();
      const base64Data = base64ToJson(data[Object.keys(data)[0]].base64);
      const headers: Record<string, string> = Object.keys(base64Data[0]).reduce(
        (acum, k) => ({ ...acum, [k]: k }),
        {}
      );
      const formattedData: Record<string, unknown>[] = base64Data.map((item: any) => ({
        ...item,
      }));
      const exportTitle = fileName || title || 'export';

      if (format === 'csv') {
        exportCSVFile(headers, formattedData, exportTitle);
      } else {
        await exportXLSXFile(headers, formattedData, exportTitle);
      }
    },
    [query, title]
  );

  if (enableXlsx) {
    return (
      <DownloadFormatMenu
        icon={<AllieDownloadIcon />}
        label={downloadLabel}
        onSelect={downloadFile}
      />
    );
  }

  return (
    <AllieReportDownloader
      title={title || 'export'}
      query={query as AllieReportDownloaderQuery}
      render={({ onClick }) => (
        <StyledIconButton
          allieSize="small"
          allieType="secondary"
          corner="rounded-edges-plus"
          onClick={onClick}
          style={{ padding: '10px 14px', borderRadius: '8px' }}
        >
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <AllieText type="body2" weight={600} color="neutral-70">
              {downloadLabel}
            </AllieText>
            <StyledDownloadIcon>
              <AllieDownloadIcon />
            </StyledDownloadIcon>
          </div>
        </StyledIconButton>
      )}
    />
  );
};
